import { YouTubeScriptService } from 'services/script/youtube-script.service'
import {
    IEmbedConfig,
    VideoProviderShortType,
    YTPlayerConfig,
    YTPlayerConfigEvent
} from '../types'
import { AbstractPlayer } from './abstract-player'
import { logger } from 'utils/system/logger'
import { getEmbedConfig, setAdConfigTargeting } from '../util'
import { YTPlayerVars } from '../constants'
import { PlaylistStore } from '../playlist/store'

/**
 * YouTube video player.
 * This makes YouTube player iframe dynamically
 */
export class YouTubePlayer extends AbstractPlayer {
    protected player: YT.Player

    // From Analytics
    private didVideoEnd = false
    private didVideoStart = false

    public constructor(
        protected element: HTMLElement,
        protected events: YTPlayerConfigEvent,
        protected isLeadVideo = false
    ) {
        super(element, events, isLeadVideo)
    }

    /**
     * When the user meets the element on their screen,
     * load script and initialize the player.
     */
    public render(): void {
        new YouTubeScriptService()
            .loadYTIframeAPI()
            .then((loaded) => {
                if (loaded) {
                    this.init().catch(console.error)
                }
            })
            .catch((err) => logger('error', err))
    }

    private async initializer(): Promise<void> {
        const events: YTPlayerConfigEvent = {
            onReady: (event: YT.PlayerEvent): void => {
                this.setMute(this.mute)
                if (this.events.onReady) {
                    this.events.onReady(event)
                }
            },
            onError: (event: YT.OnErrorEvent): void => {
                this.onError(event)
                if (this.events.onError) {
                    this.events.onError(event)
                }
            },
            onStateChange: (event: YT.OnStateChangeEvent): void => {
                this.onChange(event)
            },
            onAdEnd: (event: YT.PlayerEvent): void => {
                this.onAdEnd(event)
            },
            onPlaybackQualityChange: (
                event: YT.OnPlaybackQualityChangeEvent
            ): void => {
                this.playbackQuality = event.data
            }
        }

        const channelId = this.element.dataset.channelId || ''
        const host = this.element.dataset.host || ''

        let embedConfig: IEmbedConfig = this.isLeadVideo
            ? await getEmbedConfig()
            : await getEmbedConfig({ channelId, autoNav: true })

        // add additional Ad targeting for Player Ad Request
        embedConfig = setAdConfigTargeting(embedConfig, {
            player: VideoProviderShortType.YouTube
        })

        // Player configs
        const option: YTPlayerConfig = {
            embedConfig: embedConfig,
            width: '480',
            height: '360',
            playerVars: {
                ...YTPlayerVars,
                origin: host || window.location.origin,
                autoplay: this.autoplay
                    ? 1 // AutoPlay
                    : 0, // NoAutoPlay
                autohide: this.controls
                    ? 2 // HideProgressBar
                    : 1, // HideAllControls
                controls: this.controls
                    ? 1 // ShowLoadPlayer
                    : 0, // Hide
                loop: this.loop
                    ? 1 // Loop
                    : 0 // SinglePlay
            },
            videoId: this.videoId
        }

        if (this.isLeadVideo) {
            const leadVideo = PlaylistStore.getInstance().playlist
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            option.playerVars!.playlist = leadVideo.playlistData.items
                .map((item) => item.videoId)
                .join(',')

            option.videoId = leadVideo.playlistData.items[0].videoId
        }

        this.player = new YT.Player(this.element.id, {
            ...option,
            events
        })
    }

    /**
     * Create the YT.Player with an option
     *
     * @returns {Promise<void>}
     */
    public async init(): Promise<void> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (window.YT && (window.YT as any).loaded) {
            void this.initializer()
        } else {
            this.activateObserver()
        }
    }

    /**
     * Get the playlist index.
     *
     * @returns {Error} Should be implemented
     */
    public get index(): number {
        return this.player.getPlaylistIndex()
    }

    private onChange(event: YT.OnStateChangeEvent): void {
        this.updateEventData(event)

        switch (event.data) {
            case YT.PlayerState.ENDED:
                this.didVideoEnd = true
                this.dispatchAnalyticsComplete()
                break

            case YT.PlayerState.PLAYING:
                this.setAutoPlayByState()
                this.didVideoStart = true
                this.didVideoEnd = false
                this.dispatchAnalyticsPlay(this.autoplay)

                if (this.events.onPlay) {
                    this.events.onPlay()
                }
                return

            case YT.PlayerState.PAUSED:
                this.autoplay = false
                this.dispatchAnalyticsPaused()

                if (this.events.onPause) {
                    this.events.onPause()
                }
                return

            case YT.PlayerState.CUED:
                if (this.autoplay) {
                    this.play()
                }
                return
        }
    }

    public get title(): string {
        // Type YT.Player doesn't have getVideoData, but the real one has
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (this.player as any).getVideoData()['title']
    }

    public pause(): void {
        if (this.player && this.getState() == 'playing') {
            this.player.pauseVideo()
        }
    }
    public play(): void {
        if (this.player) {
            this.player.playVideo()
        }
    }
    public playVideoAt(index: number): void {
        if (this.player) {
            this.player.playVideoAt(index)
        }
    }
    public setMute(mute: boolean): void {
        if (this.player) {
            if (mute) {
                this.player.mute()
            } else {
                this.player.unMute()
            }
        }
    }
    // YouTube doesn't have the setter for the current player
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public setControls(_: boolean): void {
        return
    }
    public setLoop(loop: boolean): void {
        if (this.player) {
            this.player.setLoop(loop)
        }
    }
    // YouTube doesn't have the setter for the current player
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public setAutoplay(_: boolean): void {
        return
    }
    public destroy(): void {
        if (this.player) {
            this.player.destroy()
        }
    }
    public cuePlaylist(playlistArgs: YT.IPlaylistSettings): void {
        if (this.player) {
            this.player.cuePlaylist(playlistArgs)
        }
    }
    public isPausable(): boolean {
        return this.autoplay
    }
    public getState(): string {
        if (!this.player.getPlayerState) {
            return 'idle'
        }

        const state = this.player.getPlayerState()
        if (state == 2) {
            return 'paused'
        }
        if (state == 1) {
            return 'playing'
        }
        return 'idle'
    }

    // From Analytics
    /**
     * Get YouTube id from YouTube url
     *
     * @param {string} videoUrl
     * @returns {string} YouTube video ID
     */
    private getYouTubeID(videoUrl: string): string {
        const matches = videoUrl.match(/[?&]v=([^&]+)/)
        if (matches && matches.length >= 2) {
            return matches[1]
        }
        return ''
    }

    /**
     * Update YouTube Event Data. This function should be called at the beginning of any handle function
     *
     * @param {YT.PlayerEvent} playerEvent
     */
    private updateEventData(playerEvent: YT.PlayerEvent): void {
        const player = playerEvent.target
        const playerIframe: HTMLIFrameElement = player.getIframe()
        this.iframeData = JSON.parse(playerIframe.dataset.evtVal || '{}')
        // get the current video id, otherwise fallback to static video id in data-evt-val attribute in DOM
        const videoUrl = player.getVideoUrl()
        this.videoId = this.getYouTubeID(videoUrl)
        this.videoTitle = this.title
        this.isVolumeEnabled = !player.isMuted()
    }

    /**
     * Change the isAutoPlay value
     */
    private setAutoPlayByState(): void {
        /**
         * Playlist video, not the first time a video starts, another video just finished
         * autoplay = true because in a playlist a new video will start right after one finishes
         */
        if (this.isLeadVideo && this.didVideoStart && this.didVideoEnd) {
            this.autoplay = true
            return
        }

        /**
         * Playlist video, not the first time a video starts, another video
         * was interrupted before it finishes
         * autoplay = false because obviously the user clicked play or clicked on
         * another video in the list
         */
        if (this.isLeadVideo && this.didVideoStart && !this.didVideoEnd) {
            this.autoplay = false
            return
        }

        /**
         * Story video, not the first time a video starts.
         * whether it's paused then resumed, or user reloaded video,
         * or user clicked on another suggested video, it's a user interaction
         */
        if (!this.isLeadVideo && this.didVideoStart) {
            this.autoplay = false
            return
        }
    }

    /**
     * Handle the player Ad End event
     *
     * @param {YT.PlayerEvent} event
     */
    protected onAdEnd(event: YT.PlayerEvent): void {
        this.setAutoPlayByState()
        this.updateEventData(event)
        super.onAdEnd()
    }

    /**
     * Handle the player error event
     *
     * @param {YT.OnErrorEvent} event
     */
    public onError(event: YT.OnErrorEvent): void {
        /* eslint-disable no-magic-numbers */
        switch (event.data) {
            case 2: // YT.PlayerError.InvalidParam
                this.updateEventData(event)
                this.dispatchAnalyticsError(
                    'The request contains an invalid parameter value.'
                )
                return
            case 5: // YT.PlayerError.Html5Error
                this.updateEventData(event)
                this.dispatchAnalyticsError(
                    'The requested content cannot be played in an HTML5 player or another error related to the HTML5 player has occurred.'
                )
                return
            case 100: // YT.PlayerError.VideoNotFound
                this.updateEventData(event)
                this.dispatchAnalyticsError(
                    'The video requested was not found.'
                )
                return
            case 101: // YT.PlayerError.EmbeddingNotAllowed
            case 150: // YT.PlayerError.EmbeddingNotAllowed2
                this.updateEventData(event)
                this.dispatchAnalyticsError(
                    'The owner of the requested video does not allow it to be played in embedded players.'
                )
                return
        }
        /* eslint-enable no-magic-numbers */
    }
}
